<script setup>
import { computed, ref } from 'vue';
import { bestTimeGrid, heatLevel, formatNumber } from './metricsHelpers';

const props = defineProps({
  slots: { type: Array, required: true },
  dayLabels: { type: Array, required: true },
});

// Degraus sequenciais de um só azul (claro → escuro); tokens do tema viram no modo escuro.
const LEVEL_CLASSES = [
  'bg-n-slate-3',
  'bg-n-blue-4',
  'bg-n-blue-6',
  'bg-n-blue-8',
  'bg-n-blue-9',
  'bg-n-blue-11',
];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const hovered = ref(null);

const data = computed(() => bestTimeGrid(props.slots));
const cellLevel = (day, hour) =>
  heatLevel(data.value.grid[day][hour]?.avg_engagement, data.value.max);

// Resumo para leitor de tela: o próprio grid não é navegável célula a célula,
// então o rótulo do container já entrega o melhor horário em texto.
const bestSlot = computed(() =>
  props.slots.reduce(
    (best, slot) =>
      !best || (slot.avg_engagement || 0) > (best.avg_engagement || 0)
        ? slot
        : best,
    null
  )
);
const gridLabel = computed(() => {
  if (!bestSlot.value) return props.dayLabels.join(', ');
  return `${props.dayLabels[bestSlot.value.day_of_week]} ${bestSlot.value.hour}h`;
});
</script>

<template>
  <div
    class="relative flex flex-col gap-1 overflow-x-auto"
    role="img"
    :aria-label="gridLabel"
  >
    <div
      v-for="(dayLabel, day) in dayLabels"
      :key="day"
      class="flex items-center gap-1"
    >
      <span class="w-9 shrink-0 text-[11px] text-n-slate-11">{{
        dayLabel
      }}</span>
      <span
        v-for="hour in HOURS"
        :key="hour"
        data-test="cell"
        :data-level="cellLevel(day, hour)"
        aria-hidden="true"
        class="size-4 shrink-0 rounded-sm"
        :class="LEVEL_CLASSES[cellLevel(day, hour)]"
        @mouseenter="hovered = { day, hour, slot: data.grid[day][hour] }"
        @mouseleave="hovered = null"
      />
    </div>
    <div class="flex gap-1 pl-10 text-[10px] text-n-slate-10">
      <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
      <span
        v-for="hour in HOURS"
        :key="hour"
        class="w-4 shrink-0 text-center"
        >{{ hour % 6 === 0 ? `${hour}h` : '' }}</span
      >
    </div>
    <p v-if="hovered" data-test="tooltip" class="text-xs text-n-slate-11">
      <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
      {{ dayLabels[hovered.day] }} {{ hovered.hour }}h ·
      <span class="font-semibold tabular-nums text-n-slate-12">{{
        formatNumber(hovered.slot?.avg_engagement)
      }}</span>
    </p>
  </div>
</template>
